"use client";

// Route-segment error boundary. Catches render-time errors thrown anywhere below
// the root layout (e.g. the T-081 /ice/detail crash class) and shows friendly UI
// instead of a white screen. Renders INSIDE the root layout, so it inherits fonts,
// globals.css, and Providers, and can reuse app components.

import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/nextjs";
import Background_Primary from "@/components/background_primary";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Move focus to the heading on mount so screen-reader / keyboard users are
  // oriented to the new content instead of focus silently falling to <body>.
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    Sentry.captureException(error);
    headingRef.current?.focus();
  }, [error]);

  return (
    <Background_Primary>
      <div className="bg-primary-light rounded-t-[30px] mt-5 px-3 py-8 min-h-screen flex flex-col items-center justify-center text-center gap-4">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-base font-semibold text-gray-900 outline-none"
        >
          Something went wrong
        </h1>
        <p className="max-w-xs text-sm text-gray-600">
          An unexpected error occurred. Please try again.
        </p>
        <button
          type="button"
          onClick={reset}
          className="mt-2 rounded-[30px] bg-primary px-6 py-3 font-semibold text-white"
        >
          Try again
        </button>
      </div>
    </Background_Primary>
  );
}
